import { create } from 'zustand';
import { ClickContext, ClickableType } from '@utils/enums';
import { planAt, exitTargetingMode, enterTargetingMode } from '@services/actionService';
import { enterDeployMode, exitDeployMode, deployAt } from '@services/deployService';
import { killUnitApi } from '@api/debugApi';
import useGameStore from '@store/useGameStore';
import useMapStore from '@store/useMapStore';
import feedback from '@components/ui/general/feedback/feedback';

const useSelectionStore = create((set, get) => ({
  // --- CORE STATE ---
  clickContext: ClickContext.SELECT,
  previousContext: null,
  currentClick: null,
  previousClick: null,
  targetingAction: null,  // Store the action being targeted

  // --- MAIN CLICK HANDLER ---
  handleClick: (clickDTO) => {
    const state = get();

    // console.log('[Selection] Click received:', {
    //   type: clickDTO.type,
    //   context: state.clickContext,
    //   data: clickDTO.data
    // });

    // Auto-save previous context when changing
    if (state.clickContext !== state.previousContext) {
      set({ previousContext: state.clickContext });
    }

    // Don't update currentClick when:
    // 1. Clicking actions in SELECT mode
    // 2. Clicking tiles/units during TARGET_ACTION mode (keep unit selected)
    // 3. Clicking tiles during DEPLOY_UNIT mode (keep deployable selected)
    const shouldUpdateClick = !(
      (state.clickContext === ClickContext.SELECT && clickDTO.type === ClickableType.ACTION) ||
      (state.clickContext === ClickContext.TARGET_ACTION && (clickDTO.type === ClickableType.TILE || clickDTO.type === ClickableType.UNIT)) ||
      (state.clickContext === ClickContext.DEPLOY_UNIT && clickDTO.type === ClickableType.TILE)
    );

    if (shouldUpdateClick) {
      // console.log('[Selection] Updating currentClick from:', state.currentClick, 'to:', clickDTO);
      set({
        previousClick: state.currentClick,
        currentClick: clickDTO,
      });
    } else {
      // console.log('[Selection] NOT updating currentClick, keeping:', state.currentClick);
    }

    // Route by context
    switch (state.clickContext) {
      case ClickContext.SELECT:
        get().handleSelectMode(clickDTO);
        break;

      case ClickContext.DEPLOY_UNIT:
        get().handleDeployMode(clickDTO);
        break;

      case ClickContext.TARGET_ACTION:
        get().handleTargetingMode(clickDTO);
        break;

      case ClickContext.DEBUG_KILL:
        get().handleDebugKillMode(clickDTO);
        break;

      default:
        console.warn(`Unknown click context: ${state.clickContext}`);
    }
  },

  // --- SELECT MODE HANDLER ---
  handleSelectMode: (clickDTO) => {
    // console.log('[SELECT MODE] Handling:', clickDTO.type);

    switch (clickDTO.type) {
      case ClickableType.UNIT:
        // console.log('[SELECT] Unit clicked:', clickDTO.data);
        // console.log('[SELECT] Unit data details:', {
        //   hasData: !!clickDTO.data,
        //   dataKeys: clickDTO.data ? Object.keys(clickDTO.data) : [],
        //   dataId: clickDTO.data?.id,
        //   dataName: clickDTO.data?.name,
        //   fullData: JSON.stringify(clickDTO.data)
        // });
        // Just selecting a unit - InfoPanel will show it
        break;

      case ClickableType.DEPLOYABLE:
        // console.log('[SELECT] Deployable unit clicked:', clickDTO.data);
        enterDeployMode(clickDTO.data);
        // Also update currentClick to UNIT type so UnitInfoPanel shows
        set({
          currentClick: { ...clickDTO, type: ClickableType.UNIT }
        });
        break;

      case ClickableType.ACTION:
        // console.log('[SELECT] Action clicked:', clickDTO.data);
        // Action clicks are now handled by ActionsPanel using enterTargetingMode
        // This case shouldn't normally be reached anymore
        break;

      case ClickableType.PLANNED_ACTION:
        // console.log('[SELECT] Planned action clicked:', clickDTO.data);
        // Just selecting planned actions - InfoPanel will show them
        break;

      case ClickableType.STRUCTURE:
        // console.log('[SELECT] Structure clicked:', clickDTO.data);
        // Just selecting structure - InfoPanel will show it
        break;

      case ClickableType.TILE:
      case ClickableType.TERRAIN:
        // console.log('[SELECT] Empty tile/terrain clicked, clearing selections');
        get().clearAllSelections();
        break;

      default:
        console.warn(`Unknown clickable type in select mode: ${clickDTO.type}`);
    }
  },

  // --- DEPLOY MODE HANDLER ---
  handleDeployMode: (clickDTO) => {
    // console.log('[DEPLOY MODE] Handling:', clickDTO.type);

    switch (clickDTO.type) {
      case ClickableType.TILE: {
        // console.log('[DEPLOY] Deploying unit at tile:', clickDTO.data);
        const { x, y } = clickDTO.data;
        deployAt(x, y);
        break;
      }

      case ClickableType.DEPLOYABLE:
        // console.log('[DEPLOY] Switching to different unit:', clickDTO.data);
        enterDeployMode(clickDTO.data);
        // Also update currentClick to UNIT type so UnitInfoPanel shows
        set({
          currentClick: { ...clickDTO, type: ClickableType.UNIT }
        });
        break;

      default:
        // console.log('[DEPLOY] Cancelling deployment');
        exitDeployMode();
    }
  },

  // --- TARGETING MODE HANDLER ---
  handleTargetingMode: (clickDTO) => {
    // console.log('[TARGET MODE] Handling:', clickDTO.type);
    const state = get();

    // console.log('[TARGET MODE] Current state:', {
    //   currentClick: state.currentClick,
    //   currentClickType: state.currentClick?.type,
    //   targetingAction: state.targetingAction,
    //   clickContext: state.clickContext
    // });

    switch (clickDTO.type) {
      case ClickableType.TILE: {
        // console.log('[TARGET] Targeting tile:', clickDTO.data);
        // console.log('[TARGET] About to call planAt with:', {
        //   x: clickDTO.data.x,
        //   y: clickDTO.data.y,
        //   currentClick: state.currentClick,
        //   selectedUnitCheck: state.currentClick?.type === ClickableType.UNIT ? state.currentClick.data : null
        // });
        // Call planAt from actionService to execute the action
        const { x, y } = clickDTO.data;
        planAt(x, y);
        break;
      }

      case ClickableType.UNIT: {
        // console.log('[TARGET] Targeting unit:', clickDTO.data);
        // Call planAt with unit position
        const unit = clickDTO.data;
        planAt(unit.x, unit.y);
        break;
      }

      case ClickableType.ACTION: {
        console.log('[TARGET] Switching to different action:', clickDTO.data);
        // Use enterTargetingMode to switch actions
        const currentUnit = state.currentClick?.data;
        if (currentUnit) {
          enterTargetingMode(clickDTO.data, currentUnit);
        }
        break;
      }

      default:
        console.log('[TARGET] Cancelling targeting');
        // Use exitTargetingMode from actionService
        exitTargetingMode();
    }
  },

  // --- DEBUG KILL MODE HANDLER ---
  handleDebugKillMode: async (clickDTO) => {
    console.log('[DEBUG KILL MODE] Handling:', clickDTO.type);

    switch (clickDTO.type) {
      case ClickableType.UNIT:
        console.log('[DEBUG] Killing unit:', clickDTO.data);
        const gameId = useGameStore.getState().game?.id;
        const unitId = clickDTO.data?.id;

        if (!gameId || !unitId) {
          feedback.error('Cannot kill unit: missing game or unit ID');
          return;
        }

        try {
          feedback.info(`Killing unit ${unitId}...`);
          const response = await killUnitApi(gameId, unitId);
          if (response?.success) {
            feedback.success('Unit killed!');
          }
        } catch (error) {
          console.error('[DEBUG] Kill unit failed:', error);
          feedback.error('Failed to kill unit: ' + (error.message || 'Unknown error'));
        }
        break;

      default:
        console.log('[DEBUG] Can only kill units in debug mode');
    }
  },

  // --- HELPER METHODS ---
  revertToPreviousContext: () => {
    const { previousContext } = get();
    const { clearVisuals } = useMapStore.getState();

    console.log('[Selection] Reverting to previous context:', previousContext);

    clearVisuals();

    set({
      clickContext: previousContext || ClickContext.SELECT,
      previousContext: null,
    });
  },

  clearAllSelections: () => {
    console.log('[Selection] Clearing all selections');
    set({
      currentClick: null,
      previousClick: null,
      clickContext: ClickContext.SELECT,
      previousContext: null,
    });
  },

  setClickContext: (context) => {
    const state = get();
    // console.log('[Selection] Setting context:', context);
    if (state.clickContext !== context) {
      set({
        previousContext: state.clickContext,
        clickContext: context
      });
    }
  },

  setTargetingAction: (action) => {
    const state = get();
    // console.log('[Selection] Setting targeting action:', action?.slug);
    // console.log('[Selection] Current state before setTargetingAction:', {
    //   currentClick: state.currentClick,
    //   currentClickType: state.currentClick?.type,
    //   currentClickData: state.currentClick?.data
    // });
    set({
      targetingAction: action,
      clickContext: ClickContext.TARGET_ACTION
    });

    // Check state after setting
    const newState = get();
    // console.log('[Selection] State after setTargetingAction:', {
    //   currentClick: newState.currentClick,
    //   currentClickType: newState.currentClick?.type,
    //   currentClickData: newState.currentClick?.data
    // });
  },

  clearTargeting: () => {
    // console.log('[Selection] Clearing targeting');
    set({
      targetingAction: null,
      clickContext: ClickContext.SELECT
    });
  },

  // Get selected unit for actionService compatibility
  getSelectedUnit: () => {
    const state = get();
    if (state.currentClick?.type === ClickableType.UNIT) {
      return state.currentClick.data;
    }
    return null;
  },
}));

export default useSelectionStore;